/**
 * LogbookModule - Digital logbook and voyage management
 * 
 * MODULE HEADERS:
 * - Container: mb-8
 * - Title: text-2xl font-bold text-gray-900
 * - Subtitle: mt-1 text-sm text-gray-500
 * 
 * CONTENT CARDS:
 * - Container: bg-white shadow rounded-lg
 * - Header: px-6 py-5 border-b border-gray-200
 * - Header text: text-lg font-medium text-gray-900
 * - Body: p-6 or divide-y divide-gray-200 for lists
 * 
 * LAYOUT:
 * - 2/3 + 1/3 grid layout matching other modules
 * - Main content on left, sidebar on right
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TabNavigation from '../TabNavigation';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../contexts/NavigationBackContext';
import { useStorageState } from '../../../hooks/useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { useRequirements } from '../../../logic/regulations';
import { ENABLE_DAILY_CHECKS_ALL_VESSELS } from '../../../config/featureFlags';
import LogbookTabCompact from './logbook/LogbookTabCompact';
import VoyageManagementTab from './logbook/VoyageManagementTab';
import PassagePlanningTab from './logbook/PassagePlanningTab';
import EnvironmentalTab from './operations/EnvironmentalTab';
import SpecialOperationsTab from './operations/SpecialOperationsTab';
// import DailyChecksCard from './logbook/DailyChecksCard';
import { OperationsModule } from './operations';
import { addAuditEntry, AUDIT_ACTIONS, AUDIT_MODULES } from '../../../utils/audit';

// FT-356: setActiveModule threaded from TailwindMainContent so the top-level
// LogbookFullView can register a "back to dashboard" handler with the shell.
const LogbookModule = ({ setActiveModule }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module,
  // regardless of active tab. LogbookFullView still pushes its own handler
  // on top when mounted (FT-356) — that handler pops back to this one on
  // unmount.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  const [activeTab, setActiveTab] = useState('logbook');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeVoyage, setActiveVoyage] = useState(null);
  
  const { currentVesselId, getCurrentVessel, getVesselSettings } = useVessel();
  const currentVessel = getCurrentVessel();
  
  // Get vessel settings for operations configuration
  const vesselSettings = getVesselSettings();
  
  // Use base keys - vessel scoping handled by useStorageState/useLegacyStorage
  const { value: logEntries = [], setValue: saveLogEntries } = useStorageState('log_entries', []);
  const { value: voyages = [], setValue: saveVoyages } = useStorageState('voyages', []);
  const { value: passagePlans = [], setValue: savePassagePlans } = useStorageState('passage_plans', []);
  const { showAlert, showConfirm, AlertDialog, ConfirmDialog } = useCustomDialogs();

  // Update active voyage when voyages change
  useEffect(() => {
    const active = voyages.find(v => !v.endTime);
    setActiveVoyage(active);
  }, [voyages]);

  // One-time cleanup of duplicate VOYAGE_END entries on mount
  useEffect(() => {
    if (logEntries.length === 0) return;

    const seen = new Set();
    const uniqueEntries = [];
    let hadDuplicates = false;
    
    logEntries.forEach(entry => {
      // Create a unique key for VOYAGE_END entries to prevent duplicates
      let key;
      if (entry.type === 'VOYAGE_END' || entry.summary?.includes('Voyage ended')) {
        // For voyage end entries, only allow one per voyage
        key = `${entry.type}_${entry.voyageId || 'no-voyage'}`;
      } else {
        // For other entries, use type, summary and rough timestamp (minute precision)
        const timestamp = new Date(entry.timestamp);
        timestamp.setSeconds(0, 0);
        key = `${entry.type}_${entry.summary}_${timestamp.getTime()}`;
      }
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueEntries.push(entry);
      } else {
        hadDuplicates = true;
      }
    });
    
    if (hadDuplicates) {
      saveLogEntries(uniqueEntries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // Simple static tabs - Special Operations tab handles all vessel-specific operations
  // Check if daily checks are required for this vessel
  const operationsRequirements = useRequirements('operations');
  const requiredByRegs = operationsRequirements.some(
    r => r.id === 'daily_checks_inland_passenger' && r.required
  );
  
  // Feature flag override
  const enabledByOverride = ENABLE_DAILY_CHECKS_ALL_VESSELS;
  const showDailyChecks = requiredByRegs || enabledByOverride;

  const tabs = [
    { id: 'logbook', label: 'Digital Logbook' },
    { id: 'voyage', label: 'Voyage Management' },
    { id: 'planning', label: 'Passage Planning' },
    { id: 'environmental', label: 'Environmental' },
    { id: 'special', label: 'Special Operations' },
    ...(showDailyChecks ? [{ id: 'dailychecks', label: 'Daily Checks' }] : [])
  ];

  // Add log entry
  const handleAddLogEntry = async (entry) => {
    const newEntry = {
      ...entry,
      id: `LOG${Date.now()}`,
      timestamp: new Date().toISOString(),
      vesselId: currentVesselId,
      vesselName: currentVessel?.name || 'Unknown Vessel'
    };

    const updatedEntries = [newEntry, ...logEntries];
    const success = await saveLogEntries(updatedEntries);
    
    if (success) {
      // Add audit entry
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: AUDIT_ACTIONS.CREATE,
        details: `Added ${entry.type} log entry`,
        metadata: { entryId: newEntry.id, type: entry.type }
      });
    }
    
    return success;
  };

  // Delete all entries from today
  const handleClearTodayEntries = async () => {
    const confirmed = await showConfirm(
      'Clear Today\'s Entries?',
      'This will delete all log entries from today. This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const filteredEntries = logEntries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      entryDate.setHours(0, 0, 0, 0);
      return entryDate.getTime() !== today.getTime();
    });
    
    const success = await saveLogEntries(filteredEntries);
    
    if (success) {
      const deletedCount = logEntries.length - filteredEntries.length;
      await showAlert('Success', `Deleted ${deletedCount} entries from today`);
      
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: AUDIT_ACTIONS.DELETE,
        details: `Cleared ${deletedCount} log entries from today`,
        metadata: { date: today.toISOString() }
      });
    }
    
    return success;
  };

  // Remove duplicate entries
  const handleRemoveDuplicates = async () => {
    const seen = new Set();
    const uniqueEntries = [];
    let duplicatesCount = 0;
    
    logEntries.forEach(entry => {
      // Create a unique key for VOYAGE_END entries to prevent duplicates
      let key;
      if (entry.type === 'VOYAGE_END' || entry.summary?.includes('Voyage ended')) {
        // For voyage end entries, only allow one per voyage
        key = `${entry.type}_${entry.voyageId || 'no-voyage'}`;
      } else {
        // For other entries, use type, summary and rough timestamp (minute precision)
        const timestamp = new Date(entry.timestamp);
        timestamp.setSeconds(0, 0);
        key = `${entry.type}_${entry.summary}_${timestamp.getTime()}`;
      }
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueEntries.push(entry);
      } else {
        duplicatesCount++;
      }
    });
    
    if (duplicatesCount > 0) {
      const confirmed = await showConfirm(
        'Remove Duplicate Entries?',
        `Found ${duplicatesCount} duplicate entries. Remove them?`
      );
      
      if (confirmed) {
        await saveLogEntries(uniqueEntries);
        await showAlert('Success', `Removed ${duplicatesCount} duplicate entries`);
      }
    } else {
      await showAlert('No Duplicates', 'No duplicate entries found');
    }
  };

  // Start voyage or update existing voyage
  const handleStartVoyage = async (voyageData, isUpdate = false) => {
    if (isUpdate && voyageData.id) {
      // Update existing voyage
      const updatedVoyages = voyages.map(v => 
        v.id === voyageData.id ? voyageData : v
      );
      const success = await saveVoyages(updatedVoyages);
      
      if (success) {
        setActiveVoyage(voyageData);
        
        // If status changed to active, log the departure
        if (voyageData.status === 'active' && !voyages.find(v => v.id === voyageData.id)?.actualDepartureTime) {
          await handleAddLogEntry({
            type: 'VOYAGE_START',
            category: 'voyage',
            summary: `Voyage started: ${voyageData.departurePort} to ${voyageData.destinationPort}`,
            details: voyageData
          });
        }
      }
      
      return success;
    }
    
    // Create new voyage
    // End any active voyage first
    if (activeVoyage) {
      await handleEndVoyage(activeVoyage.id);
    }

    const newVoyage = {
      ...voyageData,
      id: `VOY${Date.now()}`,
      startTime: new Date().toISOString(),
      vesselId: currentVesselId,
      vesselName: currentVessel?.name || 'Unknown Vessel'
    };

    const updatedVoyages = [...voyages, newVoyage];
    const success = await saveVoyages(updatedVoyages);
    
    if (success) {
      setActiveVoyage(newVoyage);
      
      // Add log entry for voyage start
      await handleAddLogEntry({
        type: 'VOYAGE_START',
        category: 'voyage',
        summary: `Voyage started: ${voyageData.departurePort} to ${voyageData.destinationPort}`,
        details: voyageData
      });
      
      // Add audit entry
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: AUDIT_ACTIONS.CREATE,
        details: `Started voyage from ${voyageData.departurePort} to ${voyageData.destinationPort}`,
        metadata: { voyageId: newVoyage.id }
      });
    }
    
    return success;
  };

  // End voyage
  const handleEndVoyage = async (voyageId, endData = {}) => {
    const voyage = voyages.find(v => v.id === voyageId);
    if (!voyage) return false;
    
    // Prevent ending an already ended voyage
    if (voyage.endTime) return false;

    const updatedVoyage = {
      ...voyage,
      ...endData,
      endTime: new Date().toISOString()
    };

    const updatedVoyages = voyages.map(v => 
      v.id === voyageId ? updatedVoyage : v
    );
    
    const success = await saveVoyages(updatedVoyages);
    
    if (success) {
      setActiveVoyage(null);
      
      // Check if VOYAGE_END already exists for this voyage to prevent duplicates
      const hasVoyageEnd = logEntries.some(entry => 
        entry.voyageId === voyageId && 
        (entry.type === 'VOYAGE_END' || entry.summary?.includes('Voyage ended'))
      );
      
      if (!hasVoyageEnd) {
        // Add log entry for voyage end
        await handleAddLogEntry({
          type: 'VOYAGE_END',
          category: 'voyage',
          summary: `Voyage ended at ${endData.arrivalPort || 'destination'}`,
          details: updatedVoyage,
          voyageId: voyageId
        });
      }
      
      // Add audit entry
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: AUDIT_ACTIONS.UPDATE,
        details: `Ended voyage at ${endData.arrivalPort || 'destination'}`,
        metadata: { voyageId }
      });
    }
    
    return success;
  };

  // Save passage plan
  const handleSavePassagePlan = async (plan) => {
    const existingIndex = passagePlans.findIndex(p => p.id === plan.id);
    
    let updatedPlans;
    if (existingIndex >= 0) {
      updatedPlans = [...passagePlans];
      updatedPlans[existingIndex] = plan;
    } else {
      const newPlan = {
        ...plan,
        id: plan.id || `PLAN${Date.now()}`,
        createdAt: new Date().toISOString()
      };
      updatedPlans = [...passagePlans, newPlan];
    }
    
    const success = await savePassagePlans(updatedPlans);
    
    if (success) {
      await showAlert('Success', 'Passage plan saved successfully');
      
      // Add audit entry
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: existingIndex >= 0 ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
        details: `${existingIndex >= 0 ? 'Updated' : 'Created'} passage plan: ${plan.name}`,
        metadata: { planId: plan.id }
      });
    }
    
    return success;
  };

  // Delete passage plan
  const handleDeletePassagePlan = async (planId) => {
    const confirmed = await showConfirm(
      'Delete Passage Plan',
      'Are you sure you want to delete this passage plan? This action cannot be undone.'
    );
    
    if (!confirmed) return false;
    
    const updatedPlans = passagePlans.filter(p => p.id !== planId);
    const success = await savePassagePlans(updatedPlans);
    
    if (success) {
      // Add audit entry
      addAuditEntry({
        module: AUDIT_MODULES.LOGBOOK,
        action: AUDIT_ACTIONS.DELETE,
        details: `Deleted passage plan`,
        metadata: { planId }
      });
    }
    
    return success;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Digital Logbook</h1>
        <p className="mt-1 text-sm text-gray-500">
          Maintain vessel log entries, manage voyages, and plan passages
        </p>
      </div>

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="mt-6">
        {activeTab === 'logbook' && (
          <LogbookTabCompact
            logEntries={logEntries}
            activeVoyage={activeVoyage}
            voyages={voyages}
            onAddEntry={handleAddLogEntry}
            onClearToday={handleClearTodayEntries}
            onRemoveDuplicates={handleRemoveDuplicates}
            onSelectVoyage={(voyageId) => {
              // Navigate to voyage management with selected voyage
              setActiveTab('voyage');
              const voyage = voyages.find(v => v.id === voyageId);
              if (voyage) setActiveVoyage(voyage);
            }}
            // FT-356: back handler for the shell MobileBottomBar.
            onBack={setActiveModule ? () => setActiveModule('dashboard') : undefined}
          />
        )}
        
        {activeTab === 'voyage' && (
          <VoyageManagementTab
            voyages={voyages}
            activeVoyage={activeVoyage}
            passagePlans={passagePlans}
            currentVessel={currentVessel}
            logEntries={logEntries}
            onStartVoyage={handleStartVoyage}
            onEndVoyage={handleEndVoyage}
            onAddLogEntry={handleAddLogEntry}
            onOpenFullLog={() => setActiveTab('logbook')}
          />
        )}
        
        {activeTab === 'planning' && (
          <PassagePlanningTab
            passagePlans={passagePlans}
            currentVessel={currentVessel}
            onSavePlan={handleSavePassagePlan}
            onDeletePlan={handleDeletePassagePlan}
            onLinkToVoyage={(plan) => {
              setActiveTab('voyage');
              // Plan will be available in voyage tab
            }}
          />
        )}
        
        {activeTab === 'environmental' && (
          <EnvironmentalTab
            currentVessel={currentVessel}
          />
        )}
        
        {activeTab === 'special' && (
          <SpecialOperationsTab
            currentVessel={currentVessel}
            vesselSettings={vesselSettings}
            onAddEntry={handleAddLogEntry}
          />
        )}
        
        {activeTab === 'dailychecks' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Daily checks temporarily disabled</p>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AlertDialog />
      <ConfirmDialog />
    </div>
  );
};

export default LogbookModule;